import { Constructable, SymbolType } from "@spawn/common";
import {
	consumer,
	GameState,
	IAssetHandle,
	IGameStateOptions,
	inject,
	LoadingState,
	Overlay,
	SpineResource,
	TextureResource,
	Ui,
} from "@spawn/core";
import {
	FreegameIdleState,
	FreegameOutroState,
	FreegameOverlay,
	FreegameService,
	FreegameSpinningState,
	FreeGameStateHooks,
} from "@spawn/freegames";
import { GoldenBonusMetadataKeys, GoldenBonusState, GoldenBonusSymbolTypes } from "@spawn/golden-bonus";
import { ModernUi } from "@spawn/modern-ui";
import { HighlightEvents, RevealState, RevealSymbolMetaDataKeys } from "@spawn/reveal";
import {
	AfterPartyState,
	AmbientSoundController,
	AmbientSounds,
	CheatService,
	GamblingState,
	ITeaserAnimationAddonOptions,
	ResumeState,
	SlotMachineGame,
	SymbolMetaData,
	SymbolMetaDataKeys,
	SymbolModel,
	TeaserAddon,
} from "@spawn/slot-machine";
import Victor from "victor";
import { BaseSymbolTypes, SymbolTypes } from "./services/symbolService";
import { BankruptState } from "./states/bankruptState";
import { FreegameInitialNudgingState } from "./states/freegameInitialNudgingState";
import { FreegameInitialSpinningState } from "./states/freegameInitialSpinningState";
import { FreegameIntroState } from "./states/freegameIntroState";
import { FreegameNudgeState } from "./states/freegameNudgeState";
import { FreegameWinPartyState } from "./states/freegameWinPartyState";
import { GameStateHooks } from "./states/gameStateHooks";
import { SlotMachineIdleState } from "./states/idleState";
import { SpinningState } from "./states/spinningState";
import { WinPartyState } from "./states/winPartyState";

declare const SPAWN_BUILD_MODE: string | undefined;

type SpineAnimation = [string, SpineResource, string?];

/** The symbol art that the shared symbol models need. The game `SymbolService` supplies it. */
export interface INrgSymbolArt {
	readonly scatterAnimation: IAssetHandle<SpineResource>;
	readonly mysteryAnimation: IAssetHandle<SpineResource>;
	getSymbolOversizeOffset(symbolType: string): Victor | undefined;
	getSymbolLayerPriority(symbolType: string): number | undefined;
	getTextureForSymbolType(symbolType: SymbolType): TextureResource;
	getWinAnimationForSymbolType(symbolType: SymbolType): SpineAnimation | undefined;
	getTeaserAddon(symbolType: string): { constructor: typeof TeaserAddon; options: ITeaserAnimationAddonOptions } | undefined;
	getBonusAnimationExtra(symbolType: SymbolType): unknown;
	getCoverAnimation(symbolType: SymbolType): unknown;
	getRevealAnimationForSymbolType(symbolType: SymbolType): unknown;
	getRevealTextureForSymbolType(symbolType: SymbolType): unknown;
	getDelayUntilRevealAnimation(symbolType: SymbolType): unknown;
	getMysteryFrameTexture(): unknown;
	getGoldenTeaserAnimation(symbolType: SymbolType): unknown;
	getGoldenStopSoundIndex(symbolType: SymbolType): unknown;
}

/**
 * Base of the game classes: the UI, the state machine and the shared symbol metadata.
 * A game supplies its overlays and its symbol art, can replace the win party states, and adds
 * its own symbol metadata in `symbolMetaData`.
 */
@consumer
export abstract class NrgGame extends SlotMachineGame {
	@inject protected readonly cheatService!: CheatService;
	@inject protected readonly freegameService!: FreegameService;
	@inject protected readonly ambientSoundController!: AmbientSoundController;

	protected abstract readonly symbolService: INrgSymbolArt;

	constructor() {
		super();
		this.freegameService.addOverlay(FreegameOverlay.Intro, this.freegameIntroView());
		this.freegameService.addOverlay(FreegameOverlay.Outro, this.freegameOutroView());
	}

	protected abstract freegameIntroView(): Constructable<Overlay>;
	protected abstract freegameOutroView(): Constructable<Overlay>;

	protected createWinPartyState(options: IGameStateOptions): GameState {
		return new WinPartyState(options);
	}

	protected createFreegameWinPartyState(options: IGameStateOptions): GameState {
		return new FreegameWinPartyState(options);
	}

	protected createGameStateHooks(): GameStateHooks {
		return new GameStateHooks();
	}

	public onGameStart(): void {
		// If you don't want to play the intro sound every time, you can check the override setting here.
		if (this.overridesService.volumeAdjustment > 0) {
			this.ambientSoundController.playSound(this.freegameService.isActive ? AmbientSounds.SideGame : AmbientSounds.BaseGame);
		}
	}

	override createUi(): Ui {
		if (SPAWN_BUILD_MODE === "development") {
			this.cheatService.availableSymbols = [...Object.values(BaseSymbolTypes), ...Object.values(GoldenBonusSymbolTypes)];
			// eslint-disable-next-line node/no-unsupported-features/es-syntax
			void import(/* webpackChunkName: "debug-ui" */ "@spawn/debug-ui").then(
				({ DebugUi }) => new DebugUi(document.body.appendChild(document.createElement("div")))
			);
		}
		const modernUI = new ModernUi(document.body.appendChild(document.createElement("div")));
		modernUI.create();

		return modernUI;
	}

	protected override registerGameStates(): void {
		const gameStateHooks = this.createGameStateHooks();
		const fgHooks = new FreeGameStateHooks();

		this.gameStateController.addStates(
			////////////////////////////////////////////////////////////
			// BASEGAME
			new LoadingState(),
			new ResumeState(gameStateHooks.ResumeHooks()),
			new BankruptState(),
			new SlotMachineIdleState(),
			new SpinningState(gameStateHooks.SpinningHooks()),
			this.createWinPartyState(gameStateHooks.WinPartyHooks(fgHooks)),
			new AfterPartyState(),
			new GamblingState(),

			////////////////////////////////////////////////////////////
			// Reveal and Golden Bonus
			new RevealState({ onComplete: () => GoldenBonusState }),
			new GoldenBonusState(gameStateHooks.GoldenBonusHooks()),

			////////////////////////////////////////////////////////////
			// FREEGAMES
			new FreegameIntroState({
				onEnter: () => this.eventDispatcher.dispatch({ type: HighlightEvents.ResetHighlightFeature }),
				onComplete: () => FreegameInitialNudgingState, // always goto initial nudging state
			}),
			new FreegameInitialNudgingState(),
			new FreegameInitialSpinningState(),
			new FreegameIdleState({
				// normally idle state leads only to spinning or outro, so before spinning we need to settle nudging
				onComplete: (nextState) => (nextState.name === FreegameSpinningState.name ? FreegameNudgeState : nextState),
			}),
			new FreegameSpinningState(gameStateHooks.FGSpinningHooks()),
			this.createFreegameWinPartyState(gameStateHooks.FGWinpartyHooks()),
			new FreegameNudgeState(),
			new FreegameOutroState(gameStateHooks.FreegameOutroHooks())
		);
	}

	protected override createSymbolModels(): void {
		for (const symbolType of [...Object.values(BaseSymbolTypes), ...Object.values(GoldenBonusSymbolTypes)]) {
			this.symbolModelProvider.add(
				new SymbolModel({
					symbolType,
					defaultSymbolTypes: [SymbolMetaDataKeys.BaseTexture, SymbolMetaDataKeys.TeaserAddon],
					metaData: this.symbolMetaData(symbolType),
				})
			);
		}
	}

	/** The metadata of a symbol. A game adds its own keys (uncover, nudge) and can replace shared keys. */
	protected symbolMetaData(symbolType: SymbolTypes): SymbolMetaData {
		return {
			[SymbolMetaDataKeys.OversizeOffset]: this.symbolService.getSymbolOversizeOffset(symbolType),
			[GoldenBonusMetadataKeys.LayerPriority]: this.symbolService.getSymbolLayerPriority(symbolType),
			[SymbolMetaDataKeys.BaseTexture]: this.symbolService.getTextureForSymbolType(symbolType),
			[SymbolMetaDataKeys.WinAnimation]: this.symbolService.getWinAnimationForSymbolType(symbolType),

			// Freegames
			[SymbolMetaDataKeys.TeaserAddon]: this.symbolService.getTeaserAddon(symbolType),
			[SymbolMetaDataKeys.ScatterAnimation]:
				symbolType === BaseSymbolTypes.Scatter ? ["scatter_bg_trigger", this.symbolService.scatterAnimation()] : undefined,
			["BonusAnimationExtra"]: this.symbolService.getBonusAnimationExtra(symbolType),

			// Reveal
			[RevealSymbolMetaDataKeys.CoverAnimation]: this.symbolService.getCoverAnimation(symbolType),
			[RevealSymbolMetaDataKeys.RevealedAnimation]: this.symbolService.getRevealAnimationForSymbolType(symbolType),
			[RevealSymbolMetaDataKeys.RevealedTexture]: this.symbolService.getRevealTextureForSymbolType(symbolType),
			[RevealSymbolMetaDataKeys.DelayUntilRevealAnimation]: this.symbolService.getDelayUntilRevealAnimation(symbolType),
			[RevealSymbolMetaDataKeys.RevealedFrameTexture]: this.symbolService.getMysteryFrameTexture(),
			// test: real teaser is handled in TeaserAddon, this is only for the animation when we first start FG
			["MysteryTeaserAnimation"]:
				symbolType === BaseSymbolTypes.Mystery ? ["mystery_teaser", this.symbolService.mysteryAnimation()] : undefined,

			// Golden Bonus
			[GoldenBonusMetadataKeys.GoldenTeaserAnimation]: this.symbolService.getGoldenTeaserAnimation(symbolType),
			[GoldenBonusMetadataKeys.GoldenStopSoundIndex]: this.symbolService.getGoldenStopSoundIndex(symbolType),
		};
	}
}
